import {
  type Participant,
  type ParticipantTask,
  ParticipantTaskStatus,
} from '@piccolohealth/pbs-common';
import { ChakraV3, Empty, Spin } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { FaPlus } from 'react-icons/fa';
import { showModal } from '../../components/generic/Modal';
import { PiccoloError } from '../../components/generic/PiccoloError';
import { useParticipantTasksQuery } from '../../graphql/hooks/useParticipantQuery';
import { useUpdateParticipantTaskMutation } from '../../graphql/hooks/useUpdateParticipantTaskMutation';
import { useAppContext } from '../../hooks/useAppContext';
import { usePermission } from '../../hooks/usePermission';
import { ParticipantTaskCreateModal } from './ParticipantTaskCreateModal';
import { ParticipantTaskItemList } from './ParticipantTaskItemList';
import { ParticipantTasksFromTemplatesCreateModal } from './ParticipantTasksFromTemplatesCreateModal';

interface Props {
  participant: Participant;
  isDisabled: boolean;
}

export const ParticipantTaskCard = (props: Props) => {
  const { participant } = props;

  const { successToast, errorToast } = useAppContext();
  const createParticipantTaskPermission = usePermission('create', 'participantTask');
  const isDisabled = props.isDisabled || !createParticipantTaskPermission;

  const [tasksPrime, setTasksPrime] = React.useState<ParticipantTask[]>([]);
  const { data, isLoading, error } = useParticipantTasksQuery({
    organizationId: participant.organizationId,
    participantId: participant.id,
  });

  const updateParticipantTaskMutation = useUpdateParticipantTaskMutation({});

  const tasks = React.useMemo(() => {
    return (data?.organization?.participant?.tasks ?? []) as ParticipantTask[];
  }, [data?.organization?.participant?.tasks]);

  const [incompleteTasks, completeTasks] = React.useMemo(() => {
    return P.partition(tasks, (task) => task.status === ParticipantTaskStatus.Incomplete);
  }, [tasks]);

  React.useEffect(() => {
    setTasksPrime(tasks);
  }, [tasks]);

  const onChangeStatus = async (task: ParticipantTask) => {
    const existingTasks = tasksPrime;

    const updatedTask: ParticipantTask = {
      ...task,
      status:
        task.status === ParticipantTaskStatus.Incomplete
          ? ParticipantTaskStatus.Complete
          : ParticipantTaskStatus.Incomplete,
    };

    const updatedTasks = tasksPrime.map((t) => (t.id === task.id ? updatedTask : t));
    setTasksPrime(updatedTasks);

    await updateParticipantTaskMutation
      .mutateAsync({
        organizationId: participant.organizationId,
        participantId: participant.id,
        participantTaskId: task.id,
        request: {
          status: updatedTask.status,
        },
      })
      .then(() => {
        successToast(
          `Task marked as ${updatedTask.status === ParticipantTaskStatus.Complete ? 'complete' : 'incomplete'}`,
        );
      })
      .catch((err) => {
        setTasksPrime(existingTasks);
        errorToast(`Error updating task status: ${err.message}`);
      });
  };

  const onClickAddTask = () => {
    showModal(ParticipantTaskCreateModal, { participantId: participant.id });
  };

  const onClickAddTasksFromTemplates = () => {
    showModal(ParticipantTasksFromTemplatesCreateModal, { participantId: participant.id });
  };

  const content = P.run(() => {
    if (isLoading) {
      return <Spin />;
    }

    if (error) {
      return <PiccoloError h='full' error={error} />;
    }

    if (P.isEmpty(tasks)) {
      return <Empty h='full' title='No tasks created yet' p={0} />;
    }

    return (
      <>
        <ChakraV3.Stack gap={3}>
          <ChakraV3.Text fontWeight='bold'>Upcoming tasks ({incompleteTasks.length})</ChakraV3.Text>
          <ParticipantTaskItemList
            tasks={incompleteTasks}
            onChangeStatus={onChangeStatus}
            initialVisibleCount={5}
            isDisabled={isDisabled}
          />
        </ChakraV3.Stack>
        <ChakraV3.Stack gap={3}>
          <ChakraV3.Text fontWeight='bold'>Complete tasks ({completeTasks.length})</ChakraV3.Text>
          <ParticipantTaskItemList
            tasks={completeTasks}
            onChangeStatus={onChangeStatus}
            initialVisibleCount={5}
            isDisabled={isDisabled}
          />
        </ChakraV3.Stack>
      </>
    );
  });

  return (
    <ChakraV3.Card.Root h='full'>
      <ChakraV3.Card.Header>
        <ChakraV3.Stack gap={0}>
          <ChakraV3.Heading size='xl' fontWeight='bold'>
            Tasks
          </ChakraV3.Heading>
          <ChakraV3.Text fontSize='sm' color='secondary'>
            {`Manage this participant's tasks`}
          </ChakraV3.Text>
        </ChakraV3.Stack>
      </ChakraV3.Card.Header>
      <ChakraV3.Card.Body>
        <ChakraV3.Stack gap={3}>
          {content}
          <ChakraV3.HStack>
            <ChakraV3.Button
              onClick={onClickAddTasksFromTemplates}
              flex={1}
              size='sm'
              disabled={isDisabled}
              variant='subtle'
            >
              <FaPlus />
              Add tasks from templates
            </ChakraV3.Button>
            <ChakraV3.Button
              onClick={onClickAddTask}
              flex={1}
              size='sm'
              disabled={isDisabled}
              variant='subtle'
            >
              <FaPlus />
              Add task
            </ChakraV3.Button>
          </ChakraV3.HStack>
        </ChakraV3.Stack>
      </ChakraV3.Card.Body>
    </ChakraV3.Card.Root>
  );
};
